import { observable, action, computed } from 'mobx';
import { CategoryStore } from './CategoryStore';
import { NavigationItem, NavigationRoot } from '../models';
import { AuthStore } from './';
import SubscriptionStore from './SubscriptionStore';

export class NavMenuStore {
  @observable public expanded: boolean = false;
  @observable public desktopExpandedItem?: NavigationRoot;
  @observable public mobileExpandedItems: Array<NavigationRoot> = [];

  constructor(
    private categoryStore: CategoryStore,
    private authStore: AuthStore,
    private subscriptionStore: SubscriptionStore
  ) {}

  @computed
  get navigationItems() {
    const { categories } = this.categoryStore;
    const navigationItems = categories
      ? categories.map(NavigationRoot.fromCategory)
      : [];

    const { currentUser } = this.authStore;

    return [
      ...navigationItems,
      new NavigationRoot(
        `Overflow`,
        ``,
        [
          new NavigationItem(`Terms & Conditions`, `/terms`),
          new NavigationItem(`Privacy Policy`, `/privacy`),
          new NavigationItem(`Contact Us`, `/contact`),
          new NavigationItem(`About Us`, `/about`),
          currentUser
            ? new NavigationItem(
                `Log Out`,
                `#`,
                undefined,
                async (event: React.MouseEvent<HTMLAnchorElement>) => {
                  // Prevent link navigation
                  event.preventDefault();

                  // Logout
                  await this.authStore.logout();

                  if (
                    process.env.REACT_APP_ENV !== 'development' &&
                    process.env.REACT_APP_ENV !== 'test'
                  ) {
                    // In staging/production, reload the page to clean any user data out
                    // Cross-browser window.location.reload (firefox has bugs when used
                    // alongside history.replaceState - which our routing does)
                    window.location.href = window.location.href;
                  }
                }
              )
            : null
        ].filter(item => !!item) as Array<NavigationItem>,
        true
      )
    ];
  }

  @computed
  get headerNavigationItems(): Array<NavigationRoot> {
    const { currentUser, userFetchInProgress } = this.authStore;

    return [
      currentUser && currentUser.isPremium
        ? undefined
        : new NavigationItem(
            `Premier`,
            `/premier`,
            undefined,
            (event: React.MouseEvent<HTMLAnchorElement>) => {
              // Prevent link navigation
              event.preventDefault();

              // Instead use the subscription store to navigate there (which stores
              // the current URL to return the user here later)
              this.subscriptionStore.requestSignUp();
            }
          ),
      new NavigationRoot(`Search`, `/search`),
      currentUser
        ? new NavigationRoot(
            `Hi ${currentUser.firstName || currentUser.lastName}`,
            '/profile'
          )
        : userFetchInProgress
        ? null
        : new NavigationRoot(`Log In`, `/login`),
      currentUser ? null : new NavigationRoot(`Sign Up`, `/sign-up`)
    ].filter(item => !!item) as Array<NavigationRoot>;
  }

  @action
  toggle = (): void => {
    this.expanded = !this.expanded;
  };

  @action
  hide = (): void => {
    this.expanded = false;
  };

  @action
  fetchNavMenuItems = async (): Promise<void> => {
    await this.categoryStore.fetchCategories();
  };

  @action
  expandItem = (item: NavigationRoot): void => {
    this.desktopExpandedItem = item;
  };

  @action
  collapseItem = (item: NavigationRoot): void => {
    if (this.desktopExpandedItem === item) {
      this.desktopExpandedItem = undefined;
    }
  };

  @action
  collapseDesktopNav = (): void => {
    this.desktopExpandedItem = undefined;
  };

  @action
  toggleMobileItem = (item: NavigationRoot): void => {
    const itemIndex = this.mobileExpandedItems.indexOf(item);

    if (itemIndex >= 0) {
      this.mobileExpandedItems.splice(itemIndex, 1);
      return;
    }

    this.mobileExpandedItems.push(item);
  };
}

export default NavMenuStore;



// WEBPACK FOOTER //
// ./src/app/stores/NavMenuStore.ts